/**
 * Formatadores compartilhados de campos de lead (form de criacao + detalhe).
 */

import { parseDinheiro } from "./dinheiro";

// Conectivos de nomes PT-BR que ficam em minuscula quando no MEIO do nome
// (a 1a palavra sempre capitaliza). Ex.: "Marcelo Moraes de Carvalho".
const PARTICULAS_NOME = new Set(["de", "do", "da", "dos", "das", "e"]);

// Sufixos societários e siglas que NUNCA viram Title-Case, mesmo quando a
// pessoa digita tudo em minúscula.
const SIGLAS_SEMPRE = new Set([
  "ltda", "me", "epp", "sa", "eireli", "mei", "cia",
]);

/**
 * Title Case PT-BR: primeira letra de cada palavra em maiuscula, resto em
 * minuscula. "igor ribeiro" -> "Igor Ribeiro"; "MARIA" -> "Maria". Os
 * conectivos de, do, da, dos, das, e (quando NAO sao a 1a palavra) ficam
 * minusculos: "marcelo moraes de carvalho" -> "Marcelo Moraes de Carvalho".
 * Separadores de nomes (espaco, hifen, apostrofo) sao preservados. Como so
 * muda o CASE (nao o tamanho), pode ser aplicado a cada tecla sem pular o
 * cursor.
 */
/**
 * Title-Case de nome próprio — use SOMENTE no `onBlur`, nunca no `onChange`.
 *
 * V7 (auditoria 2026-08-07): aplicado a cada tecla, este helper destrói o
 * que a pessoa está digitando. Quem escreve "MDF" vê virar "Mdf" letra a
 * letra e não consegue corrigir, porque a próxima tecla re-normaliza. Nome
 * em caixa alta proposital ("SILVA & CIA") é impossível de digitar.
 *
 * No blur, o comportamento é o desejado: a pessoa digita à vontade e o
 * sistema arruma quando ela sai do campo — e, se ela voltar e insistir na
 * grafia, o valor dela permanece até o próximo blur.
 *
 * Reteste 2026-08-18: mesmo no blur, o helper destruía SIGLAS — "TST"
 * virava "Tst", "MDF" virava "Mdf". O sinal que separa sigla de caps lock
 * é a mistura de caixa no que foi digitado: se existe minúscula em algum
 * lugar do texto, as palavras inteiramente maiúsculas são intencionais e
 * ficam; se o texto TODO está em maiúscula, é caps lock e normaliza.
 */
export function capitalizarNome(valor) {
  const texto = String(valor || "");
  // Caps lock ligado: a pessoa escreveu o nome inteiro em maiúscula e o
  // que ela quer é "Maria Silva". Sem lowercase em algum lugar do texto,
  // não há sigla a preservar — normaliza tudo.
  const temMinuscula = /\p{Ll}/u.test(texto);

  let posicao = 0;
  return texto.replace(/\p{L}+/gu, (palavra) => {
    posicao += 1;
    const minuscula = palavra.toLowerCase();

    // Sufixos societários são sigla em qualquer contexto — "silva ltda"
    // vira "Silva LTDA", não "Silva Ltda".
    if (SIGLAS_SEMPRE.has(minuscula)) return minuscula.toUpperCase();

    if (posicao > 1 && PARTICULAS_NOME.has(minuscula)) return minuscula;

    // Sigla no meio de um nome digitado normalmente: preserva.
    // "TST Marcos Vieira" mantém TST; "MDF Branco" mantém MDF.
    const ehSigla = temMinuscula
      && palavra.length >= 2
      && palavra === palavra.toUpperCase()
      && /\p{Lu}/u.test(palavra);
    if (ehSigla) return palavra;

    return minuscula.charAt(0).toUpperCase() + minuscula.slice(1);
  });
}

// Régua de telefone brasileiro — espelho de `core/telefone.py` no backend
// (`normalizar_telefone_br` / `formatar_telefone_br`), com a MESMA tabela de
// casos nos testes dos dois lados. Mudou um, muda o outro.
const DDI_BRASIL = "55";
// Discado de fora no lugar do "+": "0055 11 ...".
const PREFIXO_INTERNACIONAL = "00";
// "0 11 98765-4321": o 0 de tronco/operadora.
const ZERO_DE_TRONCO = "0";
// DDD + 8 dígitos (fixo) ou 9 (celular).
const TAMANHOS_NACIONAIS = new Set([10, 11]);
// Só com 12 ou 13 dígitos um "55" inicial é DDI; com 11 ele é o DDD 55
// (Santa Maria/RS).
const TAMANHOS_COM_DDI = new Set([12, 13]);
const DIGITOS_DO_SUFIXO = 4;
// Abaixo disso não há número estrangeiro discável (DDI + assinante).
const DIGITOS_INTERNACIONAL_MIN = 8;

function semPrefixos(texto, digitos) {
  if (texto.startsWith("+")) {
    return digitos.startsWith(DDI_BRASIL) ? digitos.slice(DDI_BRASIL.length) : "";
  }
  const prefixoDdi = PREFIXO_INTERNACIONAL + DDI_BRASIL;
  if (digitos.startsWith(prefixoDdi)) return digitos.slice(prefixoDdi.length);
  if (digitos.startsWith(PREFIXO_INTERNACIONAL)) return "";
  if (digitos.startsWith(DDI_BRASIL) && TAMANHOS_COM_DDI.has(digitos.length)) {
    return digitos.slice(DDI_BRASIL.length);
  }
  if (digitos.startsWith(ZERO_DE_TRONCO)) return digitos.slice(ZERO_DE_TRONCO.length);
  return digitos;
}

/**
 * Dígitos nacionais (DDD + número) ou "" quando não é telefone brasileiro
 * completo. DDD nunca tem zero: "0800 123 4567" não vira "(80) 0123-4567".
 */
function telefoneNacional(texto) {
  const candidato = semPrefixos(texto, soDigitos(texto));
  const ehNacional = TAMANHOS_NACIONAIS.has(candidato.length)
    && candidato[0] !== "0"
    && candidato[1] !== "0";
  return ehNacional ? candidato : "";
}

/**
 * Dígitos de número estrangeiro com DDI explícito ("+1 ...", "001 ...") ou
 * "" quando não é um. "+55" pela metade não é estrangeiro.
 */
function telefoneEstrangeiro(texto) {
  const digitos = soDigitos(texto);
  let internacional = "";
  if (texto.startsWith("+")) {
    internacional = digitos;
  } else if (digitos.startsWith(PREFIXO_INTERNACIONAL)) {
    internacional = digitos.slice(PREFIXO_INTERNACIONAL.length);
  }
  const discavel = !internacional.startsWith(DDI_BRASIL)
    && internacional.length >= DIGITOS_INTERNACIONAL_MIN;
  return discavel ? internacional : "";
}

/**
 * Máscara de telefone BR: (00) 00000-0000 (celular) ou (00) 0000-0000 (fixo).
 *
 * CRM-06/FORM-07: cortava em 11 dígitos sem reconhecer o DDI, e colar
 * "+55 11 98765-4321" virava "(55) 11987-6543" — que o cockpit regravava.
 * Agora tira +55/0055/55 e o 0 de operadora antes de mascarar e NUNCA
 * descarta dígito: o que não é telefone brasileiro completo (estrangeiro,
 * sem DDD, dígito a mais, ainda pela metade) volta como veio, sem espaços
 * nas pontas. Mascarar número incompleto disfarçava o DDD que faltava.
 * Aplicar no `onBlur` (ADR-0059).
 */
export function mascaraTelefone(valor) {
  const texto = String(valor ?? "").trim();
  const nacional = telefoneNacional(texto);
  if (!nacional) return texto;
  const ddd = nacional.slice(0, 2);
  const numero = nacional.slice(2);
  const corte = numero.length - DIGITOS_DO_SUFIXO;
  return `(${ddd}) ${numero.slice(0, corte)}-${numero.slice(corte)}`;
}

/**
 * Link da conversa no WhatsApp: https://wa.me/55DDDNUMERO.
 *
 * Número incompleto, DDI do Brasil pela metade ou formato irreconhecível dão
 * `null` — melhor esconder o botão que abrir conversa com um número que não
 * existe (o que acontecia com o telefone cortado). Estrangeiro com DDI
 * explícito usa o DDI informado.
 */
export function linkWhatsapp(telefone) {
  const texto = String(telefone ?? "").trim();
  const nacional = telefoneNacional(texto);
  if (nacional) return `https://wa.me/${DDI_BRASIL}${nacional}`;
  const estrangeiro = telefoneEstrangeiro(texto);
  return estrangeiro ? `https://wa.me/${estrangeiro}` : null;
}

/**
 * Moeda BRL na convenção ÚNICA do sistema (D-6A, 2026-08-11).
 *
 * Estas duas funções ERAM máscara de centavos (direita→esquerda): digitar
 * "80000" dava **R$ 800,00**, enquanto o mesmo "80000" no campo de preço do
 * orçamento dava **R$ 80.000,00**. Duas telas do mesmo sistema divergindo
 * 100× — foi o par que apareceu no teste ao vivo (achado V8).
 *
 * Agora delegam ao utilitário canônico (`utils/dinheiro`): digitação livre
 * pt-BR, vírgula decimal, ponto milhar. Mantidas com o nome antigo de
 * propósito, para que as telas que já as importam migrem juntas sem
 * call-site órfão.
 *
 * Em campo NOVO, importe direto de `utils/dinheiro`.
 */
export function mascaraMoeda(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  // Enquanto o usuário digita, devolve o texto como está — formatar a cada
  // tecla é justamente o que fazia o cursor pular e o valor mudar de ordem
  // de grandeza. A formatação acontece no blur, via `formatarReaisBR`.
  return String(valor);
}

/** Texto digitado -> Number. Aceita "80000", "80.000,00", "R$ 1.200,50". */
export function moedaParaNumero(texto) {
  return parseDinheiro(texto);
}

/** Formata um Number (15564.21) na mascara BRL ("R$ 15.564,21"). Vazio se nulo. */
export function numeroParaMoeda(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Mantem so digitos (pra comparar telefones/valores normalizados). */
export function soDigitos(valor) {
  return String(valor || "").replace(/\D/g, "");
}
